import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { Activity, Service, Shift } from '@wilson/interfaces';
import { UpdateService } from '@wilson/services/state';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { ChangePriorityModalComponent } from '../change-priority-modal/change-priority-modal.component';
import { PriorityHelperService } from '../service/priority-tag-helper.service';
import { ServiceDetailStateModel } from '@wilson/services/interfaces';

@Component({
  selector: 'wilson-priority-tag',
  templateUrl: './priority-tag.component.html',
  styleUrls: ['./priority-tag.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PriorityTagComponent implements OnChanges {
  private readonly nzModalService = inject(NzModalService);
  private readonly translate = inject(TranslateService);
  private store = inject(Store);
  private readonly cdRef = inject(ChangeDetectorRef);
  protected priorityHelperService = inject(PriorityHelperService);

  @Input() isEditable = false;
  @Input() showNA = false;
  @Input() serviceOrShift!: Service | Shift | ServiceDetailStateModel;
  @Input() title!: string;
  @Input() isTextBold!: boolean;

  priority?: number | null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['serviceOrShift']) {
      this.priority = this.priorityHelperService.getHighestPriority(
        this.serviceOrShift?.activities as Activity[],
      );
    }
  }

  async openChangePriorityModal(): Promise<void> {
    const modalRef: NzModalRef = this.nzModalService.create({
      nzTitle: this.translate.instant('priority.change_modal.title'),
      nzContent: ChangePriorityModalComponent,
      nzClosable: false,
      nzOnCancel: () => modalRef.destroy(),
      nzData: {
        activities: this.serviceOrShift.activities,
        description: 'priority.change_modal.description',
      },
      nzFooter: [
        {
          label: this.translate.instant('general.cancel'),
          type: 'default',
          onClick: (): void => {
            modalRef.destroy();
          },
        },
        {
          label: this.translate.instant('general.save'),
          type: 'primary',
          onClick: async (
            modalInstance: ChangePriorityModalComponent,
          ): Promise<void> => {
            const result: number = modalInstance.priority!;
            this.updateServiceActivities(result);
            this.cdRef.detectChanges();
            modalRef.destroy();
          },
        },
      ],
    });
  }

  async updateServiceActivities(priority: number): Promise<void> {
    const serviceWithUpdateActivities = this.serviceOrShift.activities?.map(
      (activity) => ({
        ...activity,
        priority,
      }),
    );

    const service = {
      ...(this.serviceOrShift as ServiceDetailStateModel),
      activities: serviceWithUpdateActivities,
    };

    this.store.dispatch(new UpdateService(service));
  }
}
